import { useEffect, useId } from 'react';
import type { ScreenMessageVariant } from '@api/assistant/assistantApi';
import { endMessage, startMessage } from './pageContextStore';

export interface MessageDiagnostic {
  /** The same key the text was translated from with tr(). */
  key: string;
  /** Positional params, as shown. */
  params?: string[];
  /** A reason enum behind the message, e.g. 'SLOT_OCCUPIED'. */
  code?: string | null;
}

type Tr = (key: string, defaultText?: string, data?: any) => string;

/**
 * Translates a message and returns the matching diagnostic, so a call site names its key once:
 *   const { text, diagnostic } = trMessage(tr, 'Some.key', 'Fallback {{0}}', [value]);
 *   <COInlineMessage variant="warning" diagnostic={diagnostic}>{text}</COInlineMessage>
 * Positional params are exposed to i18next as {{0}}, {{1}}, ... as the backend's messages are.
 */
export const trMessage = (tr: Tr, key: string, defaultText: string, params: string[] = [], code: string | null = null) => ({
  text: tr(key, defaultText, Object.fromEntries(params.map((value, index) => [index, value]))),
  diagnostic: { key, params, code } as MessageDiagnostic,
});

/**
 * Records a message in the assistant's page context while it is mounted, and keeps it there for a while
 * after it unmounts (see pageContextStore). Mount-based: a message that is mounted but hidden with CSS
 * must call this only while it is shown.
 */
export const useScreenMessage = (diagnostic: MessageDiagnostic | undefined, variant: ScreenMessageVariant) => {
  const instanceId = useId();
  const key = diagnostic?.key;
  // Primitive deps, so a fresh diagnostic object on every render does not end and restart the message.
  const params = JSON.stringify(diagnostic?.params ?? []);
  const code = diagnostic?.code ?? null;

  useEffect(() => {
    if (!key) return;
    startMessage(instanceId, { key, params: JSON.parse(params), code, variant });
    return () => endMessage(instanceId);
  }, [instanceId, key, params, code, variant]);
};
